import { Button } from "@groot/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@groot/ui/dialog";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Textarea } from "@groot/ui/textarea";
import { useAccounts } from "@/hooks/api";
import { useMarkBillAsPaid } from "@/hooks/api/useBills";
import type { Bill } from "@/types";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

interface RecordPaymentDialogProps {
  bill: Bill | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RecordPaymentDialog({ bill, open, onOpenChange }: RecordPaymentDialogProps) {
  const [paymentForm, setPaymentForm] = useState({
    paidDate: dayjs().format("YYYY-MM-DDTHH:mm"),
    amount: "",
    accountId: "",
    notes: "",
  });

  const { data: accounts = [] } = useAccounts();
  const markAsPaid = useMarkBillAsPaid();

  // Reset form when bill changes
  useEffect(() => {
    if (bill) {
      setPaymentForm({
        paidDate: dayjs().format("YYYY-MM-DDTHH:mm"),
        amount: String(bill.amount),
        accountId: bill.accountId ? String(bill.accountId) : "",
        notes: "",
      });
    }
  }, [bill]);

  const handleSubmit = async () => {
    if (!bill) return;

    await markAsPaid.mutateAsync({
      id: bill.id,
      paidDate: dayjs(paymentForm.paidDate).toISOString(),
      amount: paymentForm.amount ? Number(paymentForm.amount) : undefined,
      accountId: paymentForm.accountId ? Number(paymentForm.accountId) : undefined,
      notes: paymentForm.notes || undefined,
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>Record payment for {bill?.name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="paidDate">Payment Date</Label>
            <Input
              id="paidDate"
              type="datetime-local"
              value={paymentForm.paidDate}
              onChange={(e) => setPaymentForm({ ...paymentForm, paidDate: e.target.value })}
              className="border-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              placeholder={bill ? String(bill.amount) : ""}
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
              className="border-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="accountId">Payment Account (Optional)</Label>
            <Select
              value={paymentForm.accountId || "none"}
              onValueChange={(value) =>
                setPaymentForm({
                  ...paymentForm,
                  accountId: value === "none" ? "" : value,
                })
              }
            >
              <SelectTrigger className="border-input">
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {accounts.map((account) => (
                  <SelectItem key={account.id} value={String(account.id)}>
                    {account.accountName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add any notes about this payment..."
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
              rows={3}
              className="border-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={markAsPaid.isPending}>
            {markAsPaid.isPending ? "Recording..." : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
